import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { getDateRangeFromTimeRange } from "utils/helper";
import endpoints from "../constants/endpoints";
import { getEscalations, getEscalationsSummary } from "../services/retention";
import { TaskCenterTab } from "../types";

// Prefetch escalations and escalations summary data for task center page
export const useQueryPrefetchEscalations = ({ enabled = false }) => {
  const queryClient = useQueryClient();

  // Common default filters matching taskCenterData.filters exactly
  const defaultFilters = {
    timeRange: "0",
    branch: "all",
    rankBy: "escalation_time",
    escalationTime: "Escalation time",
    showAtRisk: false,
    showInProgress: false,
    showResolved: false,
    showDismissed: false,
    showExpired: false
  };

  const prefetchEscalationsSummary = async () => {
    // Prefetch escalations summary for escalations type
    const summaryParamsEscalations: any = {
      type: TaskCenterTab.ESCALATIONS,
    };

    const summaryDateRange = getDateRangeFromTimeRange(defaultFilters.timeRange);
    Object.assign(summaryParamsEscalations, summaryDateRange);

    // Query key must match exactly: [endpoints.escalationsSummary, activeTab, filters?.timeRange, filters?.branch]
    const summaryQueryKeyEscalations = [
      endpoints.escalationsSummary, 
      TaskCenterTab.ESCALATIONS, 
      "0",  // filters.timeRange
      "all" // filters.branch
    ];

    await queryClient.prefetchQuery({
      queryKey: summaryQueryKeyEscalations,
      queryFn: () => getEscalationsSummary(summaryParamsEscalations),
      staleTime: 3 * 60 * 1000, // 3 minutes
    });

    // Prefetch escalations summary for analysis type
    const summaryParamsAnalysis: any = {
      type: TaskCenterTab.ANALYSIS,
    };

    const summaryDateRangeAnalysis = getDateRangeFromTimeRange(defaultFilters.timeRange);
    Object.assign(summaryParamsAnalysis, summaryDateRangeAnalysis);

    // Query key must match exactly: [endpoints.escalationsSummary, activeTab, filters?.timeRange, filters?.branch]
    const summaryQueryKeyAnalysis = [
      endpoints.escalationsSummary, 
      TaskCenterTab.ANALYSIS, 
      "0",  // filters.timeRange
      "all" // filters.branch
    ];

    await queryClient.prefetchQuery({
      queryKey: summaryQueryKeyAnalysis,
      queryFn: () => getEscalationsSummary(summaryParamsAnalysis),
      staleTime: 3 * 60 * 1000, // 3 minutes
    });
  };

  const prefetchEscalations = async () => {
    // Use the default filters from taskCenterData
    const status = "";
    const currentPage = 1;
    const pageSize = 10;
    const activeTab = TaskCenterTab.ESCALATIONS;

    // Get date range from timeRange filter
    const dateRange = getDateRangeFromTimeRange(defaultFilters.timeRange);

    // Prepare API parameters - exactly like TaskCenter page
    const apiParams: any = {
      status: status,
      sort_by: defaultFilters.rankBy,
      page: currentPage,
      page_size: pageSize,
      type: activeTab,
      ...dateRange
    };

    // Only include location if it's not "all"
    if (defaultFilters.branch && defaultFilters.branch !== "all") {
      apiParams.location = defaultFilters.branch;
    }

    // Query key must match exactly what's used in TaskCenter page
    const queryKey = [
      endpoints.escalations, 
      status, 
      defaultFilters.timeRange, 
      defaultFilters.rankBy, 
      defaultFilters.branch, 
      currentPage, 
      activeTab
    ];

    await queryClient.prefetchQuery({
      queryKey,
      queryFn: () => getEscalations(apiParams),
      staleTime: 3 * 60 * 1000, // 3 minutes
    });

    // Also prefetch analysis tab with same filters
    const analysisApiParams = { ...apiParams, type: TaskCenterTab.ANALYSIS };
    const analysisQueryKey = [
      endpoints.escalations, 
      status, 
      defaultFilters.timeRange, 
      defaultFilters.rankBy, 
      defaultFilters.branch, 
      currentPage, 
      TaskCenterTab.ANALYSIS
    ];

    await queryClient.prefetchQuery({
      queryKey: analysisQueryKey,
      queryFn: () => getEscalations(analysisApiParams),
      staleTime: 3 * 60 * 1000, // 3 minutes
    });
  };

  const invalidateEscalations = () => {
    queryClient.invalidateQueries({
      queryKey: [endpoints.escalations]
    });
    queryClient.invalidateQueries({
      queryKey: [endpoints.escalationsSummary]
    });
  };

  useEffect(() => {
    if (!enabled) return;

    prefetchEscalations();
    prefetchEscalationsSummary();
  }, []);

  return {
    prefetchEscalations,
    prefetchEscalationsSummary,
    invalidateEscalations
  };
};  